import React from 'react';
import { SeafarerApplicationData } from '../../../types';

interface SeafarerApplicationViewProps {
  data: SeafarerApplicationData;
}

const Field: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <div>
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900">{value || <span className="text-gray-400">—</span>}</dd>
  </div>
);

const fmtDate = (v?: string) => (v ? new Date(v).toLocaleDateString() : '');

/**
 * Renders the website's Seafarer Application Form data in the same layout the
 * applicant saw — personal info, marine passport, certificates with expiry
 * dates, and the sea-service table.
 */
const SeafarerApplicationView: React.FC<SeafarerApplicationViewProps> = ({ data }) => {
  const passport = data.marinePassport || {};
  const certificates = data.certificates || [];
  const seaService = data.seaService || [];

  return (
    <div className="space-y-6">
      {/* Personal Information */}
      <div>
        <h4 className="text-lg font-medium text-primary-700 mb-4">Personal Information / البيانات الشخصية</h4>
        <dl className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Gender / الجنس" value={data.gender} />
          <Field label="Marital Status / الحالة الاجتماعية" value={data.maritalStatus} />
          <Field label="Mobile 2 / الهاتف الثاني" value={data.mobile2} />
          <Field label="Relative Mobile / هاتف الأقارب" value={data.relativeMobile} />
          <div className="md:col-span-2">
            <Field label="Home Address / عنوان المنزل" value={data.homeAddress} />
          </div>
        </dl>
      </div>

      {/* Marine Passport */}
      <div>
        <h4 className="text-lg font-medium text-primary-700 mb-4">Marine Passport / الجواز البحري</h4>
        <dl className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field label="Marine Passport No. / رقم الجواز البحري" value={passport.number} />
          <Field label="Profession / المهنة بالجواز البحري" value={passport.profession} />
          <Field label="Date of Issue / تاريخ الإصدار" value={fmtDate(passport.issueDate)} />
          <Field label="Expiry Date / تاريخ الانتهاء" value={fmtDate(passport.expiryDate)} />
        </dl>
      </div>

      {/* Certificates */}
      {certificates.length > 0 && (
        <div>
          <h4 className="text-lg font-medium text-primary-700 mb-4">Certificates / الشهادات</h4>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Certificate / الشهادة</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Expiry Date / تاريخ الانتهاء</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {certificates.map((cert, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 text-sm text-gray-900">{cert.name}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{fmtDate(cert.expiry) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sea Service Experience */}
      {seaService.length > 0 && (
        <div>
          <h4 className="text-lg font-medium text-primary-700 mb-4">Sea Service Experience / الخبرات البحرية</h4>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Company / الشركة</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vessel / السفينة</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type / النوع</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rank / الرتبة</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">From / من</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">To / إلى</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Years / سنوات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {seaService.map((row, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-sm text-gray-900">{row.companyName || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{row.vesselName || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{row.vesselType || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{row.rank || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{fmtDate(row.from) || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{fmtDate(row.to) || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{row.experienceYears ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeafarerApplicationView;
